import { AssetTypes } from "@zapier/utils";
import { trpc } from "../trpc";
import { useMemo } from "react";

export function useTableConnectedAssets(tableId?: string) {
  const {
    data: canvases,
    isLoading: isCanvasesLoading,
    isFetching: isCanvasesFetching,
  } = trpc.table.connectedCanvases.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: interfaces,
    isLoading: isInterfacesLoading,
    isFetching: isInterfacesFetching,
  } = trpc.table.connectedInterfaces.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: chatbots,
    isLoading: isChatbotsLoading,
    isFetching: isChatbotsFetching,
  } = trpc.table.connectedChatbots.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: zaps,
    isLoading: isZapsLoading,
    isFetching: isZapsFetching,
  } = trpc.table.connectedZaps.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: subscriptions,
    isLoading: isSubscriptionsLoading,
    isFetching: isSubscriptionsFetching,
  } = trpc.table.connectedSubscriptions.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const {
    data: tableViews,
    isLoading: isTableViewsLoading,
    isFetching: isTableViewsFetching,
  } = trpc.table.connectedViews.useQuery(
    { tableId: tableId || "" },
    { enabled: !!tableId },
  );

  const transformedCanvases = useMemo(() => {
    return (
      canvases?.tableConnectedCanvases.map((canvas) => ({
        id: canvas.id,
        title: canvas.name,
        type: AssetTypes.Canvas,
      })) || []
    );
  }, [canvases]);

  const transformedInterfaces = useMemo(() => {
    return (
      interfaces?.tableConnectedInterfaces.map((interfaceObject) => ({
        id: interfaceObject.interfacesProjectId,
        title: interfaceObject.interfacesProjectName,
        type: AssetTypes.Interface,
      })) || []
    );
  }, [interfaces]);

  const transformedChatbots = useMemo(() => {
    return (
      chatbots?.tableConnectedChatbots.map((chatbot) => ({
        id: chatbot.id,
        title: chatbot.name,
        type: AssetTypes.Chatbot,
      })) || []
    );
  }, [chatbots]);

  const transformedZaps = useMemo(() => {
    return (
      zaps?.tableConnectedZaps.map((zap) => ({
        id: zap.id,
        title: zap.title || "Untitled Zap",
        type: AssetTypes.Zap,
      })) || []
    );
  }, [zaps]);

  const transformedSubscriptions = useMemo(() => {
    return (
      subscriptions?.tableSubscriptions.map((subscription) => ({
        id: subscription.connectedZapId || subscription.id,
        title: "Locked Zap",
        type: AssetTypes.TableSubscription,
      })) || []
    );
  }, [subscriptions]);

  const transformedTableViews = useMemo(() => {
    return (
      tableViews?.tableViews.map((tableView) => ({
        id: tableView.id,
        title: tableView.name,
        type: AssetTypes.TableView,
      })) || []
    );
  }, [tableViews]);

  const transformedAssets = useMemo(
    () => [
      ...transformedCanvases,
      ...transformedInterfaces,
      ...transformedChatbots,
      ...transformedZaps,
      ...transformedSubscriptions,
      ...transformedTableViews,
    ],
    [
      transformedCanvases,
      transformedInterfaces,
      transformedChatbots,
      transformedZaps,
      transformedSubscriptions,
      transformedTableViews,
    ],
  );

  const hasNonBlockingConnections =
    transformedCanvases.length > 0 ||
    transformedChatbots.length > 0 ||
    transformedZaps.length > 0;

  const hasBlockingConnections = transformedInterfaces.length > 0;

  return {
    hasNonBlockingConnections,
    hasBlockingConnections,
    hasSubscriptions: transformedSubscriptions.length > 0,
    hasViews: transformedTableViews.length > 0,
    transformedAssets,
    isLoading:
      isCanvasesLoading ||
      isInterfacesLoading ||
      isChatbotsLoading ||
      isZapsLoading ||
      isSubscriptionsLoading ||
      isTableViewsLoading,
    isFetching:
      isCanvasesFetching ||
      isInterfacesFetching ||
      isChatbotsFetching ||
      isZapsFetching ||
      isSubscriptionsFetching ||
      isTableViewsFetching,
  };
}
